/**
 * UserMenu — menu déroulant ouvert depuis l'avatar du Header.
 *
 * @remarks
 * Remplace l'ancien lien direct `NavLink → /profile` : l'avatar ouvre désormais
 * un menu. Point d'accès aux liens secondaires, légaux et à la déconnexion.
 */

import { Link, useNavigate } from 'react-router-dom';
import { LogOut, Scale, User as UserIcon } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
} from '@/shared/ui';
import { performLogout } from '@/lib/auth/logout';
import { GuidedTourMenuItem } from '@/app/guided-tour/GuidedTourMenuItem';
import { HelpMenuSubMenu } from './HelpMenuSubMenu';

/**
 * Mentions légales — pour l'instant on pointe vers la page ALT (SAAS-608).
 * À remplacer par une route interne quand la page dédiée existera.
 */
const MENTIONS_LEGALES_URL = 'https://www.alt.bzh/mentionslegales';

export function UserMenu() {
  const navigate = useNavigate();

  const handleLogout = () => {
    // logout serveur (révocation + clear cookie) puis clear local, enfin
    // redirection — le store est déjà vidé quand on navigue.
    void performLogout().finally(() => {
      void navigate('/login');
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-tour="header-user-menu"
        aria-label="Menu utilisateur"
        title="Mon compte"
        className="text-fg-muted hover:bg-bg-muted hover:text-fg data-[state=open]:bg-bg-muted data-[state=open]:text-fg inline-flex size-7 items-center justify-center rounded-md transition-colors"
      >
        <UserIcon className="size-4" aria-hidden />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild>
          <Link to="/profile">
            <Icon icon={UserIcon} size="md" />
            <span>Mon profil</span>
          </Link>
        </DropdownMenuItem>
        <GuidedTourMenuItem />
        <HelpMenuSubMenu />
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <a href={MENTIONS_LEGALES_URL} target="_blank" rel="noopener noreferrer">
            <Icon icon={Scale} size="md" />
            <span>Mentions légales</span>
          </a>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={handleLogout} className="text-error">
          <Icon icon={LogOut} size="md" />
          <span>Se déconnecter</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
